<template>
  <div class="thelook-upload-card bg-lds-bg-card rounded-lds-lg shadow-lds-lg p-8 w-full max-w-md mx-auto relative overflow-hidden border border-lds-border-default">
    <!-- Title controlled by Tenant Regions Wrapper (App.vue) -->

    <!-- Drop Zone -->
    <div
      class="upload-zone border-2 border-dashed border-lds-primary/50 rounded-lds-lg p-8 flex flex-col items-center justify-center transition-colors hover:bg-lds-primary/5 hover:border-lds-primary relative"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      :class="{ 'bg-lds-primary/10 border-lds-primary': isDragging }"
    >
      <!-- Error Overlay / Banner -->
      <div v-if="wizardStore.uploadError" class="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm">
          <div class="text-4xl mb-4">⚠️</div>
          <p class="font-lds-heading font-bold text-lds-danger text-center px-4">{{ wizardStore.uploadError }}</p>
          <button 
            @click="wizardStore.setCenterImage(null)" 
            class="mt-4 px-6 py-2 bg-lds-danger/10 hover:bg-lds-danger/20 text-lds-danger font-bold rounded-full transition-colors"
          >
            Try Again
          </button>
      </div>

      <!-- Analyzing Overlay -->
      <div v-if="wizardStore.isAnalyzing" class="absolute inset-0 bg-white/90 z-20 flex flex-col items-center justify-center backdrop-blur-sm">
          <div class="animate-spin text-4xl mb-4">🌀</div>
          <p class="font-lds-heading font-semibold text-lds-primary">Scanning Face...</p>
          <p class="text-xs text-gray-500 mt-2">Checking for bots & humans</p>
      </div>

      <!-- Success Overlay -->
      <div v-if="wizardStore.isHumanVerified" class="absolute inset-0 bg-green-50/95 z-20 flex flex-col items-center justify-center">
          <div class="text-5xl mb-4">✅</div>
          <p class="font-lds-heading font-bold text-green-600">Human Detected</p>
          <p class="text-xs text-green-500 mt-2 mb-4">Unlocking Emojis...</p>
          
          <button 
            @click="wizardStore.resetToDefaults()" 
            class="px-4 py-2 bg-white/50 hover:bg-white text-green-700 text-sm font-semibold rounded-full border border-green-200 shadow-sm transition-all flex items-center gap-2"
          >
            <span>🔄</span>
            <span>Start Over</span>
          </button>
      </div>

      <!-- Camera Icon Placeholder (replacing static image) -->
      <div class="mb-4 text-4xl">
        📷
      </div>

      <p class="text-md font-lds-body font-bold text-lds-text-main mb-1 text-center">
        Drag & drop your photo here
      </p>
      <p class="text-sm font-lds-body text-lds-text-secondary mb-6 text-center">
        or click to browse
      </p>
      
      <!-- Guidance -->
      <div class="text-xs text-orange-500 bg-orange-50 px-3 py-1 rounded-full mb-4 border border-orange-100">
        ⚠️ Ensure face is centered with space above head
      </div>
      
      <!-- Actions -->
      <div class="flex flex-col gap-3 w-full max-w-[200px]">
        <button
          @click="triggerFileSelect"
          class="btn-secondary w-full py-3 px-6 rounded-full bg-lds-primary/10 text-lds-primary font-bold hover:bg-lds-primary/20 transition-colors"
        >
          Choose Photo
        </button>

        <button
          @click="triggerCamera"
          class="btn-primary w-full py-3 px-6 rounded-full bg-lds-primary text-white font-bold hover:bg-lds-primary-hover shadow-lds-sm hover:shadow-lds-md transition-all flex items-center justify-center gap-2"
        >
          <span>Camera</span>
          <span>📷</span>
        </button>
      </div>

      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleFileChange"
      />
      
       <!-- Hidden Camera Input -->
       <input
        ref="cameraInput"
        type="file"
        accept="image/*"
        capture="user"
        class="hidden"
        @change="handleFileChange"
      />
    </div>

      <!-- Webcam Modal -->
      <div v-if="showCameraModal" class="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
          <div class="relative w-full max-w-lg bg-black rounded-3xl overflow-hidden border border-gray-800 shadow-2xl">
              <!-- Video Feed -->
              <video ref="videoRef" autoplay playsinline class="w-full h-auto object-cover aspect-square bg-gray-900"></video>
              
              <canvas ref="canvasRef" class="hidden"></canvas>

              <!-- Controls -->
              <div class="absolute bottom-6 left-0 right-0 flex justify-center items-center gap-8">
                  <button @click="stopCamera" class="bg-gray-800/80 text-white rounded-full p-4 hover:bg-gray-700 backdrop-blur-sm">
                      ❌
                  </button>
                  
                  <button @click="capturePhoto" class="bg-white rounded-full p-1 border-4 border-gray-300 hover:border-white transition-colors">
                      <div class="w-16 h-16 bg-white rounded-full border-2 border-black"></div>
                  </button>
                  
                  <!-- Toggle Face (if multiple) - Optional placeholder -->
                  <button class="bg-gray-800/80 text-white rounded-full p-4 opacity-50 cursor-not-allowed backdrop-blur-sm">
                      🔄
                  </button>
              </div>
          </div>
          <p class="text-white mt-4 font-lds-body">Position your face in the center</p>
      </div>



    <!-- Footer / Privacy -->
    <div class="mt-6 flex items-center justify-center gap-2 text-lds-text-secondary opacity-80">
      <span>🔒</span>
      <span class="text-xs font-lds-body">
        Your privacy matters - we never store your photos
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue';
import { useWizardStore } from '@emoji-photo/wizard-island';

const wizardStore = useWizardStore();
const fileInput = ref(null);
const cameraInput = ref(null);
const isDragging = ref(false);

// Webcam State
const showCameraModal = ref(false);
const videoRef = ref(null);
const canvasRef = ref(null);
let stream = null;

const triggerFileSelect = () => {
  fileInput.value?.click();
};

const triggerCamera = async () => {
    // Attempt to open Webcam Modal
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Fallback to legacy file input if not supported (e.g. some very old browsers)
        alert("Camera API not supported. Using file selector.");
        cameraInput.value?.click();
        return;
    }

    try {
        showCameraModal.value = true;
        
        try {
            // Try mobile-first (user-facing)
            stream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } } 
            });
        } catch (err) {
            // Fallback for Desktop webcams that don't support facingMode constraint
            console.warn("facingMode 'user' not supported, falling back to default camera");
            stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
        
        // Wait for DOM update
        setTimeout(() => {
            if (videoRef.value) {
                videoRef.value.srcObject = stream;
            }
        }, 100);

    } catch (err) {
        console.error("Camera Access Denied:", err);
        alert("Could not access camera. Please allow permissions or upload a file.");
        showCameraModal.value = false;
        cameraInput.value?.click();
    }
};

const stopCamera = () => {
    if (stream) {
        stream.getTracks().forEach(track => track.stop());
        stream = null;
    }
    showCameraModal.value = false;
};

const capturePhoto = () => {
    if (!videoRef.value || !canvasRef.value) return;

    const video = videoRef.value;
    const canvas = canvasRef.value;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to Blob
    canvas.toBlob((blob) => {
        if (blob) {
            // Create a nice file object
            const file = new File([blob], `webcam-capture-${Date.now()}.png`, { type: "image/png" });
            handleFile(file);
            stopCamera();
        }
    }, 'image/png');
};

// Ensure stream handles cleanup
onUnmounted(() => {
    stopCamera();
});

const handleFile = async (file) => {
    if (!file) return;
    
    console.log('[TheLook] File selected, verifying:', file.name);

    // 1. Initial Local State / Preview Setup
    const url = URL.createObjectURL(file);
    wizardStore.setCenterImage(url);

    // 2. Call Verification (Store handles population of images[0] and fileBlob on success)
    try {
        const isHuman = await wizardStore.verifyHumanity(file);
        if (isHuman) {
             wizardStore.unlockStep(3);
             
             // Auto Scroll to Next Step
             setTimeout(() => {
                const nextStep = document.querySelector('[data-island-id="EmojiIsland"]');
                if (nextStep) nextStep.scrollIntoView({ behavior: 'smooth', block: 'center' });
             }, 1500); // 1.5s delay to let user see the "Success" animation
        }
    } catch (e) {
        console.error("Verification fatal error", e);
    }
};

const handleFileChange = (event) => {
  const file = event.target.files[0];
  handleFile(file);
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file && file.type.startsWith('image/')) {
    handleFile(file);
  }
};
</script>


